import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');

const defaultBenefits = [
  'Workout from home',
  'No gym',
  'Minimal equipment',
  'Workout anytime'
];

export function Hero() {
  const [dbBanners, setDbBanners] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1440
  );
  const navigate = useNavigate();

  // Function to navigate to programs page
  const navigateToPrograms = (e) => {
    // e.stopPropagation();
    navigate('/programs');
  };



  // Fetch banners from API
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await fetch(`${API_URL}/api/banners`);
        if (response.ok) {
          const data = await response.json();
          setDbBanners(data);
        }
      } catch (error) {
        console.error('Error fetching banners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const allBanners = [...dbBanners].sort((a, b) => a.slide_order - b.slide_order);
  const isMobileViewport = viewportWidth < 768;
  const isTabletViewport = viewportWidth >= 768 && viewportWidth < 1024;
  const activeBanner = allBanners[activeSlide];
  const activeIsVideo = activeBanner?.media_type === 'video';

  // Image slides: auto-advance every 5s. Video slides: wait for ended (or manual nav).
  useEffect(() => {
    if (allBanners.length <= 1) return;
    if (activeIsVideo) return;

    const interval = setInterval(() => {
      setActiveSlide((current) => (current + 1) % allBanners.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [allBanners.length, activeSlide, activeIsVideo]);

  const goToPrevious = () => {
    setActiveSlide((current) => (current - 1 + allBanners.length) % allBanners.length);
  };

  const goToNext = () => {
    setActiveSlide((current) => (current + 1) % allBanners.length);
  };

  const handleVideoEnded = () => {
    if (allBanners.length <= 1) return;
    setActiveSlide((current) => (current + 1) % allBanners.length);
  };

  // Swipe functionality
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && allBanners.length > 1) {
      goToNext();
    }
    if (isRightSwipe && allBanners.length > 1) {
      goToPrevious();
    }
  };



  // Render Style 1 banner
  const hexToRgba = (hex, alpha = 1) => {
    if (!hex) return `rgba(0,0,0,${alpha})`;
    let c = hex.replace('#', '');
    if (c.length === 3) {
      c = c.split('').map(ch => ch + ch).join('');
    }
    const num = parseInt(c, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const resolveFontWeight = (value, fallback = 500) => {
    if (typeof value === 'number') return value;
    const map = {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700
    };
    return map[value] || fallback;
  };

  const toCssSize = (value, fallback) => {
    if (value === undefined || value === null || value === '') return fallback;
    if (typeof value === 'number') return `${value}px`;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return fallback;
      if (/^-?\d+(\.\d+)?$/.test(trimmed)) return `${trimmed}px`;
      return trimmed;
    }
    return fallback;
  };

  const clampNumericCssSize = (value, maxPx) => {
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value === 'number') return `${Math.min(value, maxPx)}px`;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return undefined;
      if (/^-?\d+(\.\d+)?$/.test(trimmed)) return `${Math.min(parseFloat(trimmed), maxPx)}px`;
      const pxMatch = trimmed.match(/^(-?\d+(\.\d+)?)px$/i);
      if (pxMatch) return `${Math.min(parseFloat(pxMatch[1]), maxPx)}px`;
      return trimmed;
    }
    return undefined;
  };

  const resolveMediaUrl = (rawPath) => {
    if (!rawPath) return '';
    if (rawPath.startsWith('http')) return rawPath;
    return `${API_URL}${rawPath}`;
  };

  const getBannerImageSrc = (banner) => {
    const rawPath = (isMobileViewport && banner.image_mobile)
      ? banner.image_mobile
      : (isTabletViewport && banner.image_tab)
        ? banner.image_tab
        : banner.image_desktop;
    return resolveMediaUrl(rawPath);
  };

  const getBannerVideoSrc = (banner) => {
    const rawPath = (isMobileViewport && banner.video_mobile)
      ? banner.video_mobile
      : (isTabletViewport && banner.video_tab)
        ? banner.video_tab
        : banner.video_desktop;
    return resolveMediaUrl(rawPath || banner.video_desktop);
  };

  const renderBannerMedia = (banner, isActive) => {
    const className = "absolute inset-0 h-full w-full object-cover";
    const alt = banner.title?.[0] || banner.title_part1 || 'Banner';

    if (banner.media_type === 'video' && (banner.video_desktop || banner.video_tab || banner.video_mobile)) {
      const src = getBannerVideoSrc(banner);
      const poster = getBannerImageSrc(banner) || undefined;
      const shouldLoop = allBanners.length <= 1;
      return (
        <video
          key={`${banner.id}-${src}`}
          className={className}
          src={src}
          poster={poster}
          muted
          playsInline
          autoPlay={isActive}
          loop={shouldLoop}
          onEnded={!shouldLoop && isActive ? handleVideoEnded : undefined}
          ref={(el) => {
            if (!el) return;
            if (isActive) {
              el.play().catch(() => {});
            } else {
              el.pause();
              try { el.currentTime = 0; } catch (_) { /* ignore */ }
            }
          }}
        />
      );
    }

    return (
      <img
        src={getBannerImageSrc(banner)}
        alt={alt}
        className={className}
      />
    );
  };

  const getBannerBenefits = (banner) => {
    const raw = banner?.benefits_items;
    if (Array.isArray(raw)) {
      const cleaned = raw.map((item) => String(item || '').trim()).filter(Boolean);
      return cleaned.length > 0 ? cleaned : defaultBenefits;
    }
    if (typeof raw === 'string' && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const cleaned = parsed.map((item) => String(item || '').trim()).filter(Boolean);
          return cleaned.length > 0 ? cleaned : defaultBenefits;
        }
      } catch (error) {
        const cleaned = raw.split('\n').map((item) => item.trim()).filter(Boolean);
        return cleaned.length > 0 ? cleaned : defaultBenefits;
      }
    }
    return defaultBenefits;
  };

  const renderStyle1Banner = (banner, index) => {
    const config = banner.style1Config;
    if (!config) return null;

    const isActive = index === activeSlide;

    // Helper function to convert hex to rgba
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Build dynamic styles
    const contentBoxStyle = {
      width: isMobileViewport ? 'min(92vw, 24rem)' : config.contentBox.width,
      height: isMobileViewport ? 'auto' : config.contentBox.height,
      padding: isMobileViewport ? '1rem' : config.contentBox.padding,
      margin: isMobileViewport ? '0' : config.contentBox.margin,
      background: `linear-gradient(to bottom right, ${hexToRgba(config.contentBox.backgroundColor1, config.contentBox.opacity)}, ${hexToRgba(config.contentBox.backgroundColor2, config.contentBox.opacity)})`,
      textAlign: config.layout.textAlignment,
      maxWidth: isMobileViewport ? '24rem' : undefined
    };

    const titleStyle = {
      fontSize: isMobileViewport
        ? clampNumericCssSize(config.title.fontSize, 44)
        : `${config.title.fontSize}px`,
      fontWeight: resolveFontWeight(config.title.fontWeight, 400),
      color: config.title.color,
      lineHeight: config.title.lineHeight,
      maxWidth: config.title.maxWidth !== 'none' ? config.title.maxWidth : undefined,
      marginLeft: config.layout.textAlignment === 'right' ? 'auto' : undefined,
      marginRight: config.layout.textAlignment === 'left' ? 'auto' : undefined,
      whiteSpace: 'pre-line'
    };

    const descriptionStyle = {
      fontSize: isMobileViewport
        ? clampNumericCssSize(config.description.fontSize, 24)
        : `${config.description.fontSize}px`,
      color: config.description.color,
      opacity: config.description.opacity,
      maxWidth: config.description.maxWidth,
      marginLeft: config.layout.textAlignment === 'right' ? 'auto' : undefined,
      marginRight: config.layout.textAlignment === 'left' ? 'auto' : undefined
    };

    const buttonStyle = {
      backgroundColor: hexToRgba(config.button.backgroundColor, config.button.backgroundOpacity),
      color: config.button.textColor,
      borderColor: hexToRgba(config.button.borderColor, config.button.borderOpacity),
      width: isMobileViewport ? '100%' : 'auto',
      maxWidth: isMobileViewport ? '100%' : config.button.maxWidth,
      padding: config.button.padding,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderRadius: '9999px',
      transition: 'all 0.2s',
      cursor: 'pointer',
      pointerEvents: 'auto',
      position: 'relative',
      zIndex: 9999
    };

    const containerClass = isMobileViewport
      ? 'absolute inset-0 w-full'
      : config.layout.contentPosition === 'right'
        ? 'absolute inset-y-0 right-0 w-full md:w-[42%] lg:w-[44%]'
        : config.layout.contentPosition === 'left'
          ? 'absolute inset-y-0 left-0 w-full md:w-[42%] lg:w-[44%]'
          : 'absolute inset-0 w-full';

    const contentClass = isMobileViewport
      ? 'relative z-10 flex h-full items-center justify-center px-4 py-6'
      : config.layout.contentPosition === 'right' ?
      'relative z-10 flex h-full items-center justify-end px-4 py-6 md:px-8 md:py-10 lg:px-10' :
      config.layout.contentPosition === 'left' ?
      'relative z-10 flex h-full items-center justify-start px-4 py-6 md:px-8 md:py-10 lg:px-10' :
      'relative z-10 flex h-full items-center justify-center px-4 py-6 md:px-8 md:py-10 lg:px-10';

    return (
      <article
        key={banner.id}
        className={`absolute inset-0 transition-all duration-700 ${
          isActive ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        {renderBannerMedia(banner, isActive)}
        <div className={containerClass} />
        <div className={contentClass}>
          <div style={contentBoxStyle}>
            {banner.title_enabled === 1 && banner.title && (
              <h1 style={titleStyle} className="mb-6">
                {banner.title[0]}{banner.title[1] ? `\n${banner.title[1]}` : ''}
              </h1>
            )}

            {banner.subtitle_enabled === 1 && banner.subtitle && (
              <p style={descriptionStyle} className="mb-8">
                {banner.subtitle}
              </p>
            )}

            {banner.cta_enabled === 1 && banner.cta?.primary && (
              <button 
                style={buttonStyle} 
                className="min-h-14"
                onClick={navigateToPrograms} data-ga-event="Navigate to programs from hero"
              >
                {banner.cta.primary}
              </button>
            )}
          </div>
        </div>
      </article>
    );
  };

  // Render non-style1 banner types (DB-driven)
  const renderOtherBanner = (banner, index) => {
    const isActive = index === activeSlide;
    const baseStyle1 = banner.style1Config || {};
    const typeStyle = banner.banner_type === 2
      ? (banner.style2Config || {})
      : banner.banner_type === 3
        ? (banner.style3Config || {})
        : {};

    const contentConfig = { ...(baseStyle1.contentBox || {}), ...(typeStyle.content || {}) };
    const titleConfig = { ...(typeStyle.title || {}), ...(baseStyle1.title || {}) };
    const descriptionConfig = { ...(typeStyle.subtitle || typeStyle.description || {}), ...(baseStyle1.description || {}) };
    const benefitsConfig = { ...(typeStyle.benefits || {}), ...(baseStyle1.description || {}) };
    const overlayConfig = typeStyle.overlay || {};
    const buttonConfig = { ...(typeStyle.button || {}), ...(baseStyle1.button || {}) };
    const overlayColor = banner.overlay?.color || '#605043';
    const overlayOpacity = banner.overlay?.opacity ?? 0.62;

    const titleStyle = {
      fontSize: isMobileViewport
        ? clampNumericCssSize(titleConfig.fontSize, 44)
        : toCssSize(titleConfig.fontSize, undefined),
      fontWeight: resolveFontWeight(titleConfig.fontWeight, 500),
      color: titleConfig.color || '#ffffff',
      lineHeight: titleConfig.lineHeight || undefined,
      maxWidth: titleConfig.maxWidth && titleConfig.maxWidth !== 'none' ? titleConfig.maxWidth : undefined
    };

    const descriptionStyle = {
      fontSize: isMobileViewport
        ? clampNumericCssSize(descriptionConfig.fontSize, 24)
        : toCssSize(descriptionConfig.fontSize, undefined),
      color: descriptionConfig.color || '#ffffff',
      opacity: descriptionConfig.opacity ?? 0.95,
      maxWidth: descriptionConfig.maxWidth && descriptionConfig.maxWidth !== 'none' ? descriptionConfig.maxWidth : undefined
    };

    const benefitsStyle = {
      fontSize: isMobileViewport
        ? clampNumericCssSize(benefitsConfig.fontSize, 22)
        : toCssSize(benefitsConfig.fontSize, undefined),
      color: benefitsConfig.color || descriptionStyle.color || '#ffffff',
      opacity: descriptionStyle.opacity
    };

    const contentCardStyle = {
      width: isMobileViewport ? 'min(92vw, 24rem)' : (contentConfig.width || undefined),
      height: isMobileViewport ? undefined : (contentConfig.height && contentConfig.height !== 'auto' ? contentConfig.height : undefined),
      padding: isMobileViewport ? '1rem' : (contentConfig.padding || undefined),
      margin: isMobileViewport ? '0' : (contentConfig.margin || undefined),
      background: `linear-gradient(to bottom right, ${hexToRgba(contentConfig.backgroundColor1 || overlayColor, contentConfig.opacity ?? overlayOpacity)}, ${hexToRgba(contentConfig.backgroundColor2 || overlayColor, contentConfig.opacity ?? overlayOpacity)})`,
      maxWidth: isMobileViewport ? '24rem' : (contentConfig.maxWidth || undefined)
    };

    const ctaButtonStyle = {
      backgroundColor: hexToRgba(buttonConfig.backgroundColor || '#d8d4dd', buttonConfig.backgroundOpacity ?? 0.8),
      color: buttonConfig.textColor || '#ffffff',
      borderColor: hexToRgba(buttonConfig.borderColor || '#ffffff', buttonConfig.borderOpacity ?? 0),
      borderWidth: '1px',
      borderStyle: 'solid',
      maxWidth: buttonConfig.maxWidth || '18rem',
      padding: buttonConfig.padding || '0.75rem 2rem',
      borderRadius: '9999px',
      pointerEvents: 'auto',
      position: 'relative',
      zIndex: 9999
    };
    const bannerBenefits = getBannerBenefits(banner);
    
    // Banner Type 2 - Hardcoded layout (now DB-driven content)
    if (banner.banner_type === 2) {
      const panelColor1 = contentConfig.backgroundColor1 || overlayConfig.color || overlayColor;
      const panelColor2 = contentConfig.backgroundColor2 || overlayConfig.color || overlayColor;
      const panelOpacity = contentConfig.opacity ?? overlayConfig.opacity ?? overlayOpacity;
      const overlayStyle = {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: isMobileViewport ? '100%' : (overlayConfig.width || contentConfig.width || '38%'),
        background: `linear-gradient(to bottom right, ${hexToRgba(panelColor1, panelOpacity)}, ${hexToRgba(panelColor2, panelOpacity)})`
      };
      return (
        <article
          key={banner.id}
          className={`absolute inset-0 transition-all duration-700 ${
            isActive ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          {renderBannerMedia(banner, isActive)}
          <div style={overlayStyle} />
          <div className="relative z-10 flex h-full items-center px-4 py-6 md:px-8 md:py-10 lg:px-16">
            <div className="w-full max-w-[32rem] my-8 md:my-16 text-white">
              {banner.title_enabled === 1 && (
                <h1
                  className="mb-6 max-w-[26rem] text-2xl font-medium leading-[1.14] text-white md:text-[2rem] lg:text-[2.2rem]"
                  style={titleStyle}
                >
                  {banner.title_part1}
                  {banner.title_part2 ? <><br />{banner.title_part2}</> : null}
                </h1>
              )}

              {banner.subtitle_enabled === 1 && banner.subtitle && (
                <p className="mb-6 text-base text-white/90 max-w-md" style={descriptionStyle}>
                  {banner.subtitle}
                </p>
              )}

              <ul className="mb-8 space-y-2">
                {bannerBenefits.map((benefit, i) => (
                  <li key={i} className="flex items-center gap-3 text-lg text-white md:text-[0.95rem]" style={benefitsStyle}>
                    <Check className="h-4 w-4 shrink-0 text-white" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>

              {banner.cta_enabled === 1 && banner.cta_primary && (
                <button 
                  className="min-h-12 rounded-full text-base font-medium text-white transition-all duration-200 md:text-[0.95rem]"
                  style={ctaButtonStyle}
                  onClick={navigateToPrograms} data-ga-event="Navigate to programs from hero"
                >
                  {banner.cta_primary}
                </button>
              )}
            </div>
          </div>
        </article>
      );
    }
    
    // Banner Type 3 - Hardcoded layout (now DB-driven content)
    if (banner.banner_type === 3) {
      return (
        <article
          key={banner.id}
            className={`absolute inset-0 transition-all duration-700 ${
            isActive ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          {renderBannerMedia(banner, isActive)}
          {/* Content - Left aligned with proper margins */}
          <div className="relative z-10 flex h-full items-center px-4 py-6 md:px-8 md:py-10 lg:px-16">
            <div
              className="w-full max-w-[32rem] px-4 py-5 md:px-8 md:py-8 lg:px-12 lg:py-12 my-8 md:my-16"
              style={contentCardStyle}
            >
              {banner.title_enabled === 1 && (
                <h1 className="mb-6 max-w-[26rem] text-2xl font-medium leading-[1.14] text-white md:text-[2rem] lg:text-[2.2rem]" style={titleStyle}>
                  {banner.title_part1} {banner.title_part2}
                </h1>
              )}
              
              {banner.subtitle_enabled === 1 && banner.subtitle && (
                <p className="text-base mb-6 text-white/90 max-w-md" style={descriptionStyle}>
                  {banner.subtitle}
                </p>
              )}
              
              {/* Benefits list */}
              <div className="mb-8 space-y-2">
                {bannerBenefits.map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3 text-lg text-white md:text-[0.95rem]" style={benefitsStyle}>
                    <Check className="h-4 w-4 shrink-0 text-white" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
              
              {banner.cta_enabled === 1 && banner.cta_primary && (
                <button 
                  className="min-h-12 rounded-full text-base font-medium text-white transition-all duration-200 md:text-[0.95rem]"
                  style={ctaButtonStyle}
                  onClick={navigateToPrograms} data-ga-event="Navigate to programs from hero"
                >
                  {banner.cta_primary}
                </button>
              )}
            </div>
          </div>
        </article>
      );
    }
    
    // Banner Type 4 - Hardcoded design (Left-aligned with purple overlay like banner type 2)
    return (
      <article
        key={banner.id}
        className={`absolute inset-0 transition-all duration-700 ${
          isActive ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        {renderBannerMedia(banner, isActive)}
        <div
          className="absolute inset-y-0 left-0 w-full md:w-[35%]"
          style={{ background: hexToRgba(overlayColor, overlayOpacity) }}
        />
        
        {/* Content - Left aligned with proper margins and moved 50px right */}
        <div className="relative z-10 flex h-full items-center">
          <div className="max-w-[32rem] px-4 py-5 md:px-8 md:py-8 lg:px-12 lg:py-12 my-8 md:my-16 ml-4 md:ml-[50px]">
            {banner.title_enabled === 1 && (
              <h1 className="mb-6 text-2xl font-semibold leading-[1.14] text-white md:text-[2rem] lg:text-[2.2rem]">
                {banner.title_part1} {banner.title_part2}
              </h1>
            )}
            
            {banner.subtitle_enabled === 1 && banner.subtitle && (
              <p className="text-base mb-6 text-white/90">
                {banner.subtitle}
              </p>
            )}
            
            {/* Benefits list */}
            <div className="mb-8 space-y-2">
              {bannerBenefits.map((benefit, i) => (
                <div key={i} className="flex items-center gap-3 text-lg text-white md:text-[0.95rem]">
                  <Check className="h-4 w-4 shrink-0 text-white" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>
            
            {banner.cta_enabled === 1 && banner.cta_primary && (
              <button 
                className="rounded-full text-base font-medium text-white transition-all duration-200 md:text-lg"
                style={ctaButtonStyle}
                onClick={navigateToPrograms} data-ga-event="Navigate to programs from hero"
              >
                {banner.cta_primary}
              </button>
            )}
          </div>
        </div>
      </article>
    );
  };

  if (loading) {
    return (
      <main className="relative min-h-screen w-full overflow-hidden">
        <section className="relative h-screen w-full flex items-center justify-center">
          <div>Loading...</div>
        </section>
      </main>
    );
  }

  if (allBanners.length === 0) {
    return (
      <main className="relative min-h-screen w-full overflow-hidden">
        <section className="relative h-screen w-full flex items-center justify-center">
          <div>No banners available</div>
        </section>
      </main>
    );
  }

  return (
    <main className="relative min-h-screen w-full overflow-hidden">
      <section 
        className="relative min-h-[100svh] w-full group"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {allBanners.length > 1 && (
          <>
            {/* Navigation buttons - visible on hover only */}
            <button
              type="button"
              aria-label="Previous slide"
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/20 border border-white/30 flex items-center justify-center text-white opacity-0 hover:opacity-100 transition-opacity duration-300 group-hover:opacity-100"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <button
              type="button"
              aria-label="Next slide"
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/20 border border-white/30 flex items-center justify-center text-white opacity-0 hover:opacity-100 transition-opacity duration-300 group-hover:opacity-100"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {allBanners.map((banner, index) => {
          if (banner.banner_type === 1) {
            return renderStyle1Banner(banner, index);
          } else {
            return renderOtherBanner(banner, index);
          }
        })}
      </section>
    </main>
  );
}

export default Hero;
